import { useCallback, useEffect, useRef, useState } from "react";
import { registerDoneSink, toast } from "../Toast/toast";
import "../../Trainer/TrainerDashboard.css";

/* =============================================================
   POPUP DONE -- what a popup shows once its change is saved

   The house rule: a change made in a popup is confirmed IN the popup.
   Its content is replaced by the kit's done panel (.pg-done -- the tick,
   the message, a Done button), no toast is raised, and the popup closes
   by itself after POPUP_DONE_SECONDS. Done closes it at once; so do the
   ×, Escape and a click outside, as before.

   usePopupDone(open) is the wiring. While `open`, the popup takes every
   toast.success raised (Toast/toast.js registerDoneSink) and `done`
   holds its text. The popup renders <PopupDone> in place of its body
   and hides its footer while `done` is set. `showDone(text)` sets it
   directly, for a save that raises no toast of its own.

   A popup the page closes in the same moment it saves never gets to
   show the panel; the message is toasted instead, so it is never lost.
   ============================================================= */

export const POPUP_DONE_SECONDS = 5;

export const usePopupDone = (open = true) => {
  const [done, setDone] = useState(null);
  // A message taken but not yet on screen.
  const pendingRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setDone(null);
      return undefined;
    }
    const unregister = registerDoneSink((text) => {
      pendingRef.current = text;
      setDone(text);
      return true;
    });
    return () => {
      unregister();
      if (pendingRef.current) {
        const text = pendingRef.current;
        pendingRef.current = null;
        toast.success(text, { toast: true });
      }
    };
  }, [open]);

  useEffect(() => {
    if (done) pendingRef.current = null;
  }, [done]);

  const showDone = useCallback((text) => setDone((d) => d || text || "Saved"), []);
  const clearDone = useCallback(() => setDone(null), []);

  return { done, showDone, clearDone };
};

const PopupDone = ({ message, text, onClose, seconds = POPUP_DONE_SECONDS }) => {
  const [left, setLeft] = useState(seconds);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => {
      const remain = seconds - Math.floor((Date.now() - started) / 1000);
      if (remain <= 0) {
        clearInterval(timer);
        closeRef.current?.();
      } else {
        setLeft(remain);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [seconds]);

  return (
    <div className="pg-done" role="status" aria-live="polite">
      <span className="pg-done-mark" aria-hidden="true" />
      <p className="pg-done-title">{message}</p>
      <p className="pg-done-text">
        {text ? `${text} ` : ""}This closes in {left} second{left === 1 ? "" : "s"}.
      </p>
      <div className="pg-done-actions">
        <button type="button" className="pg-btn pg-btn--main" onClick={() => closeRef.current?.()}>
          Done
        </button>
      </div>
    </div>
  );
};

export default PopupDone;
